import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { API, MEDIA_URL } from '../config';
import RateUsModal from './RateUsModal';
import PlanBadge from './PlanBadge';
import '../styles/theme.css';

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  const [userName, setUserName] = useState('');
  const [photoUrl, setPhotoUrl] = useState(null);
  const [unread, setUnread] = useState(0);
  const [userTier, setUserTier] = useState(localStorage.getItem('user_tier'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    const name = localStorage.getItem('user_name') || '';
    setUserName(name.split(' ')[0] || 'User');
    setUserTier(localStorage.getItem('user_tier'));
    const token = localStorage.getItem('token');
    if (token) {
      fetch(`${API}/job-seeker-profile/my-profile/`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : null).then(data => {
        if (data?.profile_photo) {
          const url = data.profile_photo.startsWith('http') ? data.profile_photo : `${MEDIA_URL}/${data.profile_photo}`;
          setPhotoUrl(url);
        }
      }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const fetchUnread = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      fetch(`${API}/messages/unread-count/`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null).then(d => { if (d) setUnread(d.unread); }).catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, []);

  const initials = userName.slice(0, 2).toUpperCase() || 'U';
  const [showRateUs, setShowRateUs] = useState(false);

  const navItems = [
    { label: 'Overview', icon: 'fa-th-large', path: '/dashboard' },
    { label: 'My Applications', icon: 'fa-paper-plane', path: '/my-applications', tour: 'applications' },
    { label: 'Matched Jobs', icon: 'fa-bullseye', path: '/matched-jobs', tour: 'matched-jobs' },
    { label: 'Browse Jobs', icon: 'fa-search', path: '/jobs', tour: 'browse-jobs' },
    { label: 'Find Companies', icon: 'fa-building', path: '/search-companies' },
    { label: 'Messages', icon: 'fa-comments', path: '/messages', badge: unread || null },
  ];

  const toolItems = [
    { label: 'My Documents', icon: 'fa-folder-open', path: '/documents' },
    { label: 'Auto-Apply', icon: 'fa-bolt', path: '/auto-apply-settings', tour: 'auto-apply' },
    { label: 'My Profile', icon: 'fa-user', path: '/profile', tour: 'my-profile' },
    { label: 'CV Optimisation', icon: 'fa-file-medical-alt', path: '/cv-optimise' },
    { label: 'Salary Insights', icon: 'fa-chart-bar', path: '/salary-insights' },
    { label: 'Interview Prep', icon: 'fa-chalkboard-teacher', path: '/interview-prep' },
    { label: 'Notifications', icon: 'fa-bell', path: '/notification-settings' },
    { label: 'My Plan', icon: 'fa-crown', path: '/pricing', tour: 'my-plan' },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user_name');
    localStorage.removeItem('user_type');
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
    {/* Mobile overlay */}
    <div
      className={`sidebar-overlay${mobileOpen ? ' visible' : ''}`}
      onClick={() => setMobileOpen(false)}
    />

    {/* Floating hamburger button — mobile only */}
    <button
      className="sidebar-hamburger"
      onClick={() => setMobileOpen(o => !o)}
      aria-label="Toggle menu"
    >
      <i className={`fas ${mobileOpen ? 'fa-times' : 'fa-bars'}`}></i>
    </button>

    <aside className={`sidebar${mobileOpen ? ' open' : ''}`}>
      <a className="sidebar-logo" onClick={() => navigate('/dashboard')}>
        <img src="/JobLinkedAI.png" alt="JobLinked AI" className="sidebar-logo-img" />
        <span className="sidebar-logo-text">Job<span>Linked</span> AI</span>
      </a>
      <nav className="sidebar-nav">
        <span className="sidebar-section-label">Main</span>
        {navItems.map(item => (
          <button
            key={item.path}
            className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => navigate(item.path)}
            data-tour={item.tour}
          >
            <i className={`fas ${item.icon}`}></i>
            {item.label}
            {item.badge > 0 && <span className="sidebar-badge">{item.badge}</span>}
          </button>
        ))}

        <span className="sidebar-section-label">Tools</span>
        {toolItems.map(item => (
          <button
            key={item.path}
            className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
            onClick={() => navigate(item.path)}
            data-tour={item.tour}
          >
            <i className={`fas ${item.icon}`}></i>
            {item.label}
          </button>
        ))}
      </nav>

      <div className="sidebar-footer">
        <button className="sidebar-link" onClick={() => setShowRateUs(true)} style={{ color: '#f59e0b' }}>
          <i className="fas fa-star"></i>
          Rate Us
        </button>
        <div className="sidebar-user">
          <div
            className="sidebar-avatar"
            onClick={() => navigate('/profile')}
            title="View my profile"
            style={{ cursor: 'pointer' }}
          >
            {photoUrl
              ? <img src={photoUrl} alt={userName} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              : initials
            }
          </div>
          <div className="sidebar-user-info" onClick={() => navigate('/profile')} style={{ cursor: 'pointer' }}>
            <div className="sidebar-user-name">{userName}</div>
            <div className="sidebar-user-role">Job Seeker</div>
            <PlanBadge tier={userTier} size="sm" />
          </div>
          <button
            onClick={handleLogout}
            title="Logout"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.4rem',
              borderRadius: '6px',
              color: '#475569',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 0.15s, background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#475569'; e.currentTarget.style.background = 'none'; }}
          >
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </div>
    </aside>
    {showRateUs && <RateUsModal onClose={() => setShowRateUs(false)} />}
    </>
  );
}

export default Sidebar;
